import { Controller } from "@hotwired/stimulus";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import * as turf from "@turf/turf";
import mapboxgl from "mapbox-gl";

export default class extends Controller {
  static targets = ["boundingBoxInput", "item", "map"];

  static values = {
    latitude: Number,
    longitude: Number,
    mapboxAccessToken: String,
    mapboxStyleUrl: String,
    markerIconUrl: String,
    markers: Array,
    searchUrl: String,
  };

  initialize() {
    this.markers = [];
    this.fitMapToItemBoundsTimeout = null;
    this.currentlyHighlightedId = null;
    this._skipNextBoundsUpdate = false;
    mapboxgl.accessToken = this.mapboxAccessTokenValue;
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
      window.map = null;
    }
  }

  mapTargetConnected() {
    this.map = window.map = new mapboxgl.Map({
      antialias: false,
      center: this.mapCenter,
      container: this.mapTarget.id,
      cooperativeGestures: true,
      dragRotate: false,
      logoPosition: "bottom-right",
      minZoom: 3,
      pitch: 0,
      pitchWithRotate: false,
      scrollZoom: true,
      style: this.mapboxStyleUrlValue,
      zoom: 4,
    });

    this.popup = new mapboxgl.Popup({
      anchor: "bottom",
      className: "mapbox-popup",
      closeButton: true,
      closeOnClick: false,
      closeOnMove: false,
      focusAfterOpen: false,
      maxWidth: "400px",
      offset: 27,
    });

    this.map.addControl(new mapboxgl.NavigationControl());

    this.map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        showAccuracyCircle: false,
        trackUserLocation: false,
      }),
    );

    map.addControl(
      new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        placeholder: "Find address",
        countries: "us",
        types: "place,address",
      }),
      "top-left",
    );

    if (this.hasCoordinates) {
      this.map.flyTo({
        animate: false,
        center: this.mapCenter,
        zoom: 10,
      });
    } else if (this.queryParams.has("within_bounds") && this.queryParams.get("within_bounds").length > 0) {
      /**
       * If a bounding box exists in the query params, fit the map to it.
       */
      const parts = this.queryParams.get("within_bounds").split(",");
      const bounds = new mapboxgl.LngLatBounds([parts[1], parts[0], parts[3], parts[2]]);
      this.map.fitBounds(bounds, { animate: false });
    } else if (
      this.queryParams.has("by_address") &&
      this.queryParams.get("by_address").length > 0 &&
      this.itemTargets.length > 0
    ) {
      this.fitMapToItemBounds();
    }

    this.map.on("load", () => {
      // Add a map marker source
      this.map.loadImage(this.markerIconUrlValue, (error, image) => {
        if (error) throw error;
        this.map.addImage("custom-marker", image);

        this.map.addSource("center_points", {
          type: "geojson",
          data: this.centerPointData,
        });

        this.map.addLayer({
          id: "center_points",
          type: "symbol",
          source: "center_points",
          layout: {
            "icon-image": "custom-marker",
            "icon-allow-overlap": true,
            "icon-offset": [0, 0],
            "icon-padding": 2,
            "icon-size": ["interpolate", ["linear"], ["zoom"], 0, 0.2, 10, 0.6],
            "symbol-placement": "point",
            "symbol-z-order": "viewport-y",
            "text-allow-overlap": false,
            "text-anchor": "top",
            "text-field": ["step", ["zoom"], "", 7, ["to-string", ["get", "name"]]],
            "text-font": ["Poppins", "Arial Unicode MS Regular"],
            "text-justify": "center",
            "text-offset": [0, 0.4],
            "text-optional": true,
            "text-padding": 2,
            "text-size": 15,
          },
          paint: {
            "text-color": "#212529",
            "text-halo-color": "hsla(0, 0%, 100%, 0.58)",
            "text-halo-width": 1.1,
            "text-halo-blur": 1.1,
          },
        });
      });

      this.map.addSource("vicinity_circles", {
        type: "geojson",
        data: this.vicinityCircleData,
      });

      this.map.addLayer({
        id: "vicinity_circles_fill",
        type: "fill",
        source: "vicinity_circles",
        paint: {
          "fill-color": "rgba(37, 99, 235, 0.15)",
        },
      });

      this.map.addLayer({
        id: "vicinity_circles_outline",
        type: "line",
        source: "vicinity_circles",
        paint: {
          "line-color": "rgba(37, 99, 235, 0.55)",
          "line-width": 2,
        },
      });

      this.map.on("mouseenter", "center_points", (e) => {
        if (!e.features || e.features.length === 0) return;
        this.map.getCanvas().style.cursor = "pointer";
        const id = e.features[0].properties.id;
        this.highlightItemById(id, true);
      });

      this.map.on("mouseleave", "center_points", (e) => {
        if (!e.features || e.features.length === 0) return;
        this.map.getCanvas().style.cursor = "";
        const id = e.features[0].properties.id;
        this.highlightItemById(id, false);
      });

      this.map.on("click", "center_points", (e) => {
        const props = e.features[0].properties;
        this.map.easeTo({
          center: e.features[0].geometry.coordinates,
          padding: {
            top: 100,
          },
        });

        this.popup.setLngLat(e.features[0].geometry.coordinates).setHTML(this.template(props)).addTo(this.map);

        this.scrollProviderCardIntoView(props.id);
      });

      const onVicinityCircleClick = (e) => {
        if (!e.features || e.features.length === 0) return;
        const props = e.features[0].properties;
        const { centerLng, centerLat } = props;
        const center = [parseFloat(centerLng), parseFloat(centerLat)];

        this.map.easeTo({
          center,
          padding: { top: 100 },
        });

        this.popup.setLngLat(center).setHTML(this.template(props)).addTo(this.map);
        this.scrollProviderCardIntoView(props.id);
      };

      this.map.on("click", "vicinity_circles_fill", onVicinityCircleClick);
      this.map.on("click", "vicinity_circles_outline", onVicinityCircleClick);

      const onVicinityCircleEnter = (e) => {
        if (!e.features || e.features.length === 0) return;
        this.map.getCanvas().style.cursor = "pointer";
        const id = e.features[0].properties.id;
        this.highlightItemById(id, true);
      };

      const onVicinityCircleLeave = (e) => {
        if (!e.features || e.features.length === 0) return;
        this.map.getCanvas().style.cursor = "";
        const id = e.features[0].properties.id;
        this.highlightItemById(id, false);
      };

      this.map.on("mouseenter", "vicinity_circles_fill", onVicinityCircleEnter);
      this.map.on("mouseleave", "vicinity_circles_fill", onVicinityCircleLeave);
      this.map.on("mouseenter", "vicinity_circles_outline", onVicinityCircleEnter);
      this.map.on("mouseleave", "vicinity_circles_outline", onVicinityCircleLeave);

      this.map.on("dragstart", this.closePopup);

      this.map.on("dragend", () => {
        this.setInputBounds();
      });

      this.map.on("zoomend", () => {
        if (this._skipNextBoundsUpdate) {
          this._skipNextBoundsUpdate = false;
          return;
        }
        this.setInputBounds();
      });

      this.map.on("boxzoomend", () => {
        this.setInputBounds();
      });

      this.map.on("zoomstart", this.closePopup);

      // Fixes a bug where the canvas is offset and leaves blank space.
      this.map.resize();

      this.fitMapToItemBoundsTimeout = setTimeout(() => {
        if (!this.hasVisibleItemsInViewport()) {
          this.fitMapToItemBounds();
        }
      }, 150);
    });
  }

  closePopup = () => {
    this.popup.remove();
  };

  fitMapToItemBounds = () => {
    const bounds = new mapboxgl.LngLatBounds();

    this.itemTargets.forEach((item) => {
      const itemLng = parseFloat(item.dataset.longitude);
      const itemLat = parseFloat(item.dataset.latitude);

      // Only add valid coordinates to the bounds
      if (!isNaN(itemLng) && !isNaN(itemLat)) {
        bounds.extend([itemLng, itemLat]);
      }
    });

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { animate: true });
    }
  };

  hasVisibleItemsInViewport = () => {
    if (!this.map || this.itemTargets.length === 0) {
      return false;
    }

    const mapBounds = this.map.getBounds();

    return this.itemTargets.some((item) => {
      const itemLng = parseFloat(item.dataset.longitude);
      const itemLat = parseFloat(item.dataset.latitude);

      // Skip items with invalid coordinates
      if (isNaN(itemLng) || isNaN(itemLat)) {
        return false;
      }

      return mapBounds.contains([itemLng, itemLat]);
    });
  };

  handleVisibilityChange() {
    // Use a small delay to ensure DOM has updated before resizing
    setTimeout(() => {
      if (this.map) {
        // Force the map to resize to fit its container
        this.map.resize();
      }
    }, 50);
  }

  itemTargetDisconnected = (el) => {
    /**
     * If an item marker is outside the bounds of the map and its
     * popup is open, close it.
     */
    if (!this.popup?.isOpen()) return;

    // Check if the item has valid coordinates before using them
    const itemLng = parseFloat(el.dataset.longitude);
    const itemLat = parseFloat(el.dataset.latitude);

    if (isNaN(itemLng) || isNaN(itemLat)) {
      return;
    }

    // If the popup is outside of the map bounds, hide it.
    if (!this.map.getBounds().contains(new mapboxgl.LngLat(itemLng, itemLat))) {
      this.fitMapToItemBounds();

      // ...if the popup is showing for this item
      const popupEl = this.popup.getElement();
      if (popupEl.querySelector(`[data-item-id="${el.dataset.id}"]`)) {
        this.popup.remove();
      }
    }
  };

  get itemTargetIds() {
    return this.itemTargets.map((item) => item.dataset.id);
  }

  get hasCoordinates() {
    return Boolean(this.latitudeValue !== 0 && this.longitudeValue !== 0);
  }

  get mapCenter() {
    if (this.latitudeValue !== 0 && this.longitudeValue !== 0) {
      return [this.longitudeValue, this.latitudeValue];
    }

    return [-98, 38.88];
  }

  /**
   * Get point data from rendered item search results. Each
   * item stores relevant data in the dataset on the DOM object.
   */
  get centerPointData() {
    return {
      type: "FeatureCollection",
      features: this.itemTargets
        .map((item) => {
          if (item.dataset.isApproximate === "true") {
            return null;
          }

          const itemLng = parseFloat(item.dataset.longitude);
          const itemLat = parseFloat(item.dataset.latitude);

          // Skip items with invalid coordinates
          if (isNaN(itemLng) || isNaN(itemLat)) {
            return null;
          }

          return {
            type: "Feature",
            properties: item.dataset,
            geometry: {
              type: "Point",
              coordinates: [itemLng, itemLat],
            },
          };
        })
        .filter(Boolean), // Remove null entries
    };
  }

  get vicinityCircleData() {
    return {
      type: "FeatureCollection",
      features: this.itemTargets
        .map((item) => {
          if (item.dataset.isApproximate !== "true") {
            return null;
          }

          const itemLng = parseFloat(item.dataset.longitude);
          const itemLat = parseFloat(item.dataset.latitude);
          const radiusMiles = parseFloat(item.dataset.radiusMiles);

          if (isNaN(itemLng) || isNaN(itemLat) || isNaN(radiusMiles) || radiusMiles <= 0) {
            return null;
          }

          const circle = turf.circle([itemLng, itemLat], radiusMiles, {
            units: "miles",
            steps: 64,
          });

          circle.properties = {
            ...item.dataset,
            centerLng: itemLng,
            centerLat: itemLat,
          };

          return circle;
        })
        .filter(Boolean),
    };
  }

  get queryParams() {
    return new URLSearchParams(document.location.search.substring(1));
  }

  highlightItemById(id, highlighted) {
    // Clear previous highlight if switching to a different item
    if (this.currentlyHighlightedId && this.currentlyHighlightedId !== id) {
      this.highlightItemById(this.currentlyHighlightedId, false);
    }

    const item = this.itemTargets.find((el) => el.dataset.id === id);
    if (!item) return;

    const card = item.querySelector("div.relative.flex.items-start");
    const target = card || item;

    if (highlighted) {
      target.classList.add("ring-2", "ring-brand-blue-500", "shadow-xl", "-translate-y-0.5");
      this.currentlyHighlightedId = id;
    } else {
      target.classList.remove("ring-2", "ring-brand-blue-500", "shadow-xl", "-translate-y-0.5");
      if (this.currentlyHighlightedId === id) {
        this.currentlyHighlightedId = null;
      }
    }
  }

  /**
   * Scroll the provider card with the given id into view (e.g. after clicking its map pin).
   * Uses the Rails dom_id convention: provider_{id}.
   */
  scrollProviderCardIntoView(id) {
    const el = document.getElementById(`provider_${id}`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    el?.focus();
  }

  renderMarkers = (_event) => {
    this.map.getSource("center_points").setData(this.centerPointData);
    this.map.getSource("vicinity_circles").setData(this.vicinityCircleData);
  };

  setInputBounds = () => {
    const bounds = this.map.getBounds();

    const value = `${bounds.getSouthWest().lat},${bounds.getSouthWest().lng},${
      bounds.getNorthEast().lat
    },${bounds.getNorthEast().lng}`;

    if (this.hasBoundingBoxInputTarget) {
      this.boundingBoxInputTarget.value = value;
      this.dispatch("bounding_box_changed");
    }
  };

  showPopupForItem = (event) => {
    if (!this.map || !this.map.loaded()) return;

    const item = event.currentTarget;
    const itemLng = parseFloat(item.dataset.longitude);
    const itemLat = parseFloat(item.dataset.latitude);

    if (isNaN(itemLng) || isNaN(itemLat)) return;

    const coordinates = [itemLng, itemLat];
    const properties = {
      id: item.dataset.id,
      popoverUrl: item.dataset.popoverUrl,
    };

    // Highlight the card when hovering
    this.highlightItemById(item.dataset.id, true);
    this.popup.setLngLat(coordinates).setHTML(this.template(properties)).addTo(this.map);
  };

  hidePopupOnLeave = (event) => {
    if (this.popup) {
      this.popup.remove();
    }
    // Clear highlight when leaving the card
    if (event && event.currentTarget) {
      this.highlightItemById(event.currentTarget.dataset.id, false);
    }
  };

  /**
   * Zoom the map to the provider's pin or circle when the provider card is clicked.
   * Does nothing if the click was on a link (e.g. "View Available Spaces").
   */
  zoomToItem(event) {
    if (event.target.closest("a")) return;
    if (!this.map?.loaded()) return;

    const item = event.currentTarget;
    const lng = parseFloat(item.dataset.longitude);
    const lat = parseFloat(item.dataset.latitude);
    if (Number.isNaN(lng) || Number.isNaN(lat)) return;

    const isApproximate = item.dataset.isApproximate === "true";
    const zoom = isApproximate ? 11 : 9;

    this._skipNextBoundsUpdate = true;
    this.map.flyTo({
      center: [lng, lat],
      // zoom,
      padding: { top: 100 },
      duration: 800,
    });

    this.popup
      .setLngLat([lng, lat])
      .setHTML(this.template({ id: item.dataset.id, popoverUrl: item.dataset.popoverUrl }))
      .addTo(this.map);
  }

  template = ({ id, popoverUrl }) => {
    return `
      <div data-item-id="${id}">
        <turbo-frame id="map_popover" src="${popoverUrl}">
          <div class="flex flex-col w-full gap-1">
            <div class="skeleton h-[30px] w-3/4"></div>
            <div class="skeleton h-5 w-full"></div>
            <div class="skeleton h-4 w-2/5"></div>
          </div>
        </turbo-frame>
      </div>
    `;
  };
}
